import { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Gauge, TreeStructure, Receipt, ShoppingCart, UsersThree,
  Truck, Vault, BookOpen, SignOut, Cow, Package, ShieldCheck, ChartLineUp,
  Bank, Calculator, CaretDown, CaretRight, Tag, Storefront, CashRegister,
  ChatCircleDots, Factory, PushPin, PushPinSlash, CreditCard, ClipboardText, Car, Path,
  Scissors, Warehouse, ArrowsLeftRight, Scales, Buildings, ForkKnife,
  MagnifyingGlass, Star, Wrench,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useSidebar } from "@/context/SidebarContext";
import { toast } from "sonner";

export const MAX_FAVORITOS = 3;

// Estrella para fijar/quitar una pantalla de los favoritos (barra negra superior).
function FavStar({ to, favoritos, onToggle }) {
  const on = favoritos.includes(to);
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(to); }}
      title={on ? "Quitar de favoritos" : `Fijar en la barra superior (máx. ${MAX_FAVORITOS})`}
      data-testid={`fav-toggle-${to.replace(/\//g, "-")}`}
      className={`shrink-0 p-1 rounded transition-opacity ${on ? "text-amber-300 opacity-100" : "opacity-0 group-hover/item:opacity-70 hover:!opacity-100 text-neutral-200"}`}
    >
      <Star size={13} weight={on ? "fill" : "regular"} />
    </button>
  );
}

// Estructura del menú: items simples + grupos con children.
// Un grupo se muestra desplegable si al menos uno de sus children está permitido.
// Grupos alineados con los permisos definidos en AdminUsersPage (Operación, Contabilidad,
// Ventas y Clientes, Compras y Proveedores, Tesorería, Administración).
const menu = [
  // --- Operación diaria (accesos rápidos, sin grupo) ---
  { to: "/pos", label: "Punto de Venta", icon: CashRegister, testid: "sidebar-pos", perm: "pos", highlight: true },
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testid: "sidebar-dashboard", perm: "dashboard" },

  // --- Contabilidad ---
  {
    key: "g-contabilidad",
    label: "Contabilidad",
    icon: Calculator,
    testid: "sidebar-contabilidad",
    children: [
      { to: "/plan-cuentas", label: "Plan de Cuentas", icon: TreeStructure, testid: "sidebar-plan-cuentas", perm: "plan_cuentas" },
      { to: "/sumas-saldos", label: "Sumas y Saldos", icon: Calculator, testid: "sidebar-sumas-saldos", perm: "plan_cuentas" },
      { to: "/asientos", label: "Asientos", icon: BookOpen, testid: "sidebar-asientos", perm: "asientos" },
      { to: "/movimientos-a-clasificar", label: "Movimientos a Clasificar", icon: BookOpen, testid: "sidebar-movimientos-a-clasificar", perm: "plan_cuentas" },
    ],
  },

  // --- Ventas y Clientes ---
  {
    key: "g-ventas",
    label: "Ventas y Clientes",
    icon: Receipt,
    testid: "sidebar-ventas-group",
    children: [
      { to: "/ventas", label: "Ventas / Facturación", icon: Receipt, testid: "sidebar-ventas-facturacion", perm: "ventas" },
      { to: "/productos", label: "Productos y Precios", icon: Package, testid: "sidebar-productos-child", perm: "productos" },
      { to: "/promociones", label: "Promociones y Descuentos", icon: Tag, testid: "sidebar-promociones", perm: "ventas" },
      { to: "/pedidos", label: "Pedidos", icon: ChatCircleDots, testid: "sidebar-pedidos", perm: "ventas" },
      { to: "/tienda-nube", label: "Tienda Nube", icon: Storefront, testid: "sidebar-tienda-nube", perm: "tienda_nube" },
      { to: "/rec", label: "Remitos Cárnicos (REC)", icon: Cow, testid: "sidebar-rec", perm: "ventas" },
      { to: "/clientes", label: "Ctas. Ctes. Clientes", icon: UsersThree, testid: "sidebar-clientes-child", perm: "clientes" },
    ],
  },

  // --- Logística ---
  {
    key: "g-logistica",
    label: "Logística",
    icon: Truck,
    testid: "sidebar-logistica-group",
    children: [
      { to: "/logistica", label: "Hoja de Reparto", icon: Path, testid: "sidebar-logistica", perm: "ventas" },
      { to: "/logistica/transportistas", label: "Transportistas", icon: Truck, testid: "sidebar-transportistas", perm: "ventas" },
      { to: "/logistica/vehiculos", label: "Vehículos", icon: Car, testid: "sidebar-vehiculos", perm: "ventas" },
    ],
  },

  // --- Producción ---
  {
    key: "g-produccion",
    label: "Producción",
    icon: Factory,
    testid: "sidebar-produccion-group",
    children: [
      { to: "/produccion/ordenes-trabajo", label: "Orden de Trabajo", icon: Factory, testid: "sidebar-ordenes-trabajo", perm: "ordenes_trabajo" },
      { to: "/produccion/recepcion", label: "Recepción de Mercadería", icon: Truck, testid: "sidebar-recepcion", perm: "recepcion_mercaderia" },
      { to: "/produccion/transformaciones", label: "Transformaciones", icon: Scissors, testid: "sidebar-transformaciones", perm: "transformaciones" },
      { to: "/produccion/recetas", label: "Recetas", icon: ForkKnife, testid: "sidebar-recetas", perm: "recetas" },
      { to: "/produccion/control-salida", label: "Control de Salida", icon: ClipboardText, testid: "sidebar-control-salida", perm: "control_salida" },
      { to: "/produccion/inventario", label: "Inventario", icon: ClipboardText, testid: "sidebar-inventario", perm: "inventario" },
      { to: "/stock", label: "Stock", icon: Warehouse, testid: "sidebar-stock", perm: "stock" },
    ],
  },

  // --- Compras y Proveedores ---
  {
    key: "g-compras",
    label: "Compras y Proveedores",
    icon: ShoppingCart,
    testid: "sidebar-compras-group",
    children: [
      { to: "/compras", label: "Compras", icon: ShoppingCart, testid: "sidebar-compras", perm: "compras" },
      { to: "/orden-compra", label: "Orden de Compra", icon: ShoppingCart, testid: "sidebar-orden-compra", perm: "compras" },
      { to: "/insumos", label: "Insumos y M. Primas", icon: Package, testid: "sidebar-insumos", perm: "insumos" },
      { to: "/proveedores", label: "Ctas. Ctes. Proveedores", icon: Truck, testid: "sidebar-proveedores", perm: "proveedores" },
    ],
  },

  // --- Tesorería ---
  {
    key: "g-tesoreria",
    label: "Tesorería",
    icon: Vault,
    testid: "sidebar-tesoreria-group",
    children: [
      { to: "/cajas", label: "Cajas y Bancos", icon: Vault, testid: "sidebar-cajas", perm: "cajas" },
      { to: "/movimientos-cajas", label: "Movimientos entre Cajas", icon: ArrowsLeftRight, testid: "sidebar-movimientos-cajas", perm: "cajas" },
      { to: "/cheques", label: "Cheques Recibidos", icon: Bank, testid: "sidebar-cheques", perm: "cajas" },
      { to: "/cheques-emitidos", label: "Cheques Emitidos", icon: Bank, testid: "sidebar-cheques-emitidos", perm: "cajas" },
      { to: "/gastos-estructura", label: "Gastos de Estructura", icon: Receipt, testid: "sidebar-gastos-estructura", perm: "gastos_estructura" },
      { to: "/cashflow-2", label: "CashFlow", icon: ChartLineUp, testid: "sidebar-cashflow-2", perm: "cashflow" },
    ],
  },

  // --- Pasivo Financiero/Fiscal ---
  {
    key: "g-pasivos",
    label: "Pasivo Fin./Fiscal",
    icon: Scales,
    testid: "sidebar-pasivos-group",
    children: [
      { to: "/pasivos/entidades", label: "Entidades", icon: Buildings, testid: "sidebar-pasivos-entidades", perm: "pasivos" },
      { to: "/pasivos/saldos", label: "Saldos de Entidades", icon: Scales, testid: "sidebar-pasivos-saldos", perm: "pasivos" },
    ],
  },

  // --- Administración ---
  {
    key: "g-admin",
    label: "Administración",
    icon: ShieldCheck,
    testid: "sidebar-admin-group",
    children: [
      { to: "/admin-users", label: "Administrador de Usuarios", icon: ShieldCheck, testid: "sidebar-admin-users", perm: "admin_users" },
      { to: "/mantenimiento", label: "Mantenimiento de Datos", icon: Wrench, testid: "sidebar-mantenimiento", perm: "admin_users" },
      { to: "/medios-pago", label: "Medios de Pago", icon: CreditCard, testid: "sidebar-medios-pago", perm: "medios_pago" },
      { to: "/etiquetas", label: "Edición de Etiquetas", icon: Tag, testid: "sidebar-etiquetas", perm: "etiquetas" },
      { to: "/especificaciones", label: "Especificaciones", icon: Tag, testid: "sidebar-especificaciones", perm: "admin_users" },
      { to: "/auditoria", label: "Auditoría de Movimientos", icon: ClipboardText, testid: "sidebar-auditoria", perm: "auditoria" },
    ],
  },

  // --- Auditoría (grupo separado) ---
  {
    key: "g-auditoria",
    label: "Auditoría",
    icon: MagnifyingGlass,
    testid: "sidebar-auditoria-group",
    children: [
      { to: "/auditoria/salida", label: "Auditoría de Salida", icon: MagnifyingGlass, testid: "sidebar-auditoria-salida", perm: "auditoria_salida" },
    ],
  },
];

// Aplanado de rutas para lookup rápido de label/icon (usado por ModuleTabs)
export const FLAT_ROUTES = menu.flatMap((it) => (it.children ? it.children : [it]))
  .filter((it) => it.to)
  .reduce((acc, it) => { acc[it.to] = { label: it.label, icon: it.icon }; return acc; }, {});

export default function Sidebar() {
  const { user, logout, setFavoritos } = useAuth();
  const { pinned, togglePin } = useSidebar();
  const nav = useNavigate();
  const location = useLocation();

  const favoritos = user?.favoritos || [];
  const toggleFav = async (to) => {
    const on = favoritos.includes(to);
    if (!on && favoritos.length >= MAX_FAVORITOS) {
      toast.warning(`Máximo ${MAX_FAVORITOS} favoritos. Quitá uno (estrella) para agregar otro.`);
      return;
    }
    try {
      await setFavoritos(on ? favoritos.filter((f) => f !== to) : [...favoritos, to]);
      toast.success(on ? "Quitado de favoritos" : "Fijado en la barra superior");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el favorito");
    }
  };

  const doLogout = async () => { await logout(); nav("/login"); };

  const allowed = (perm) => {
    if (!user) return false;
    if (user.is_admin) return true;
    return (user.permissions || []).includes(perm);
  };

  const visibleItems = menu
    .map((it) => {
      if (it.children) {
        const kids = it.children.filter((c) => allowed(c.perm));
        return kids.length ? { ...it, children: kids } : null;
      }
      return allowed(it.perm) ? it : null;
    })
    .filter(Boolean);

  // Grupos abiertos por defecto: expandidos si alguna ruta actual coincide con children
  const initialOpen = {};
  visibleItems.forEach((it) => {
    if (it.children) {
      initialOpen[it.key] = it.children.some((c) => location.pathname.startsWith(c.to));
    }
  });
  const [openGroups, setOpenGroups] = useState(initialOpen);
  const toggle = (k) => setOpenGroups((s) => ({ ...s, [k]: !s[k] }));

  return (
    <aside
      className="sidebar-dark w-64 shrink-0 h-screen sticky top-0 flex flex-col border-r border-black"
      style={{ background: "#A02836" }}
    >
      <div className="px-5 py-5 border-b border-black/20 flex items-center gap-3">
        <img
          src="https://customer-assets.emergentagent.com/job_carne-contable/artifacts/2jcyugqt_logo-1625602837-1684335351-d041931afd0dbc075ba09060badaa6f21684335352.png"
          alt="La Bife SRL"
          className="h-9 w-auto object-contain shrink-0"
          data-testid="sidebar-logo"
        />
        <div className="flex-1 min-w-0">
          <div
            className="font-heading font-bold text-[15px] uppercase"
            style={{ color: "#F5EAD8", letterSpacing: "0.12em" }}
          >
            LA BIFE SRL
          </div>
        </div>
        <button
          type="button"
          onClick={togglePin}
          title={pinned ? "Desfijar: el panel se oculta solo y aparece al llevar el mouse al borde izquierdo" : "Fijar panel lateral (queda siempre visible)"}
          data-testid="sidebar-pin-btn"
          className={`p-1.5 rounded-md transition-colors ${pinned ? "text-white bg-black/25 hover:bg-black/35" : "text-neutral-100/80 hover:text-white hover:bg-black/20"}`}
        >
          {pinned ? <PushPin size={14} weight="fill" /> : <PushPinSlash size={14} weight="bold" />}
        </button>
      </div>

      <nav className="flex-1 py-3 overflow-y-auto">
        {visibleItems.map((it) => {
          // Grupo desplegable
          if (it.children) {
            const isOpen = !!openGroups[it.key];
            const Icon = it.icon;
            const active = it.children.some((c) => location.pathname.startsWith(c.to));
            return (
              <div key={it.key} data-testid={`${it.testid}-group`}>
                <button
                  onClick={() => toggle(it.key)}
                  data-testid={it.testid}
                  className={`w-full flex items-center gap-3 px-5 py-2.5 text-[13px] font-medium hover:bg-neutral-800/50 transition-colors ${active ? "text-white" : "text-neutral-300"}`}
                >
                  <Icon size={18} weight="regular" />
                  <span className="flex-1 text-left">{it.label}</span>
                  {isOpen ? <CaretDown size={12} /> : <CaretRight size={12} />}
                </button>
                {isOpen && (
                  <div className="bg-neutral-950/40 border-l-2 border-neutral-800 ml-4">
                    {it.children.map(({ to, label, icon: CIcon, testid }) => (
                      <NavLink
                        key={to}
                        to={to}
                        data-testid={testid}
                        onClick={() => setOpenGroups((s) => ({ ...s, [it.key]: false }))}
                        className={({ isActive }) =>
                          `group/item flex items-center gap-3 pl-5 pr-3 py-2 text-[12px] font-medium hover:bg-neutral-800/70 ${isActive ? "text-white bg-neutral-800/60" : "text-neutral-400"}`
                        }
                      >
                        <CIcon size={14} weight="regular" />
                        <span className="flex-1">{label}</span>
                        <FavStar to={to} favoritos={favoritos} onToggle={toggleFav} />
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          // Item simple
          const { to, label, icon: Icon, testid, highlight } = it;
          return (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                highlight
                  ? `group/item flex items-center gap-3 px-5 py-3 text-[14px] font-bold text-white bg-emerald-700/90 hover:bg-emerald-600 border-l-4 border-emerald-400 ${isActive ? "bg-emerald-600" : ""}`
                  : `group/item flex items-center gap-3 px-5 py-2.5 text-[13px] font-medium ${isActive ? "active" : ""}`
              }
            >
              <Icon size={highlight ? 20 : 18} weight={highlight ? "bold" : "regular"} />
              <span className="flex-1">{label}</span>
              <FavStar to={to} favoritos={favoritos} onToggle={toggleFav} />
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-neutral-800 p-4">
        <div className="text-[11px] text-neutral-500 uppercase font-mono tracking-widest">Sesión</div>
        <div className="text-[13px] text-neutral-200 mt-1 truncate">
          {user?.username || user?.email}
        </div>
        {user?.is_admin && (
          <div className="text-[10px] text-[#B84A52] font-mono uppercase tracking-widest mt-0.5">
            Administrador
          </div>
        )}
        <button
          data-testid="logout-btn"
          onClick={doLogout}
          className="mt-3 flex items-center gap-2 text-[12px] text-neutral-400 hover:text-white transition-colors duration-150"
        >
          <SignOut size={16} /> Cerrar sesión
        </button>
      </div>
    </aside>
  );
}
